import { useState } from 'react';
import { ForumPost } from '../types';

interface CreatePostData {
  content: string;
}

interface PostModalProps {
  /** Null = create (reply) mode, non-null = edit mode */
  post: ForumPost | null;
  /** Topic title shown as read-only context in create mode */
  topicTitle: string;
  /** Parent post author shown as read-only context in create mode */
  parentAuthorName: string;
  onSave: (data: CreatePostData) => Promise<void>;
  onClose: () => void;
}

export default function PostModal({
  post,
  topicTitle,
  parentAuthorName,
  onSave,
  onClose,
}: PostModalProps) {
  const isEdit = post !== null;

  const [content, setContent] = useState(post?.content ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!content.trim()) {
      setError('Content is required');
      return;
    }

    setSaving(true);
    try {
      await onSave({ content: content.trim() });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal modal-blur fade show"
      style={{ display: 'block' }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{isEdit ? 'Edit Post' : 'Reply to Post'}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* Context labels — only shown in create (reply) mode */}
              {!isEdit && (
                <>
                  <div className="mb-2">
                    <span className="text-muted small">Topic: </span>
                    <span className="fw-medium">{topicTitle}</span>
                  </div>
                  <div className="mb-3">
                    <span className="text-muted small">Replying to: </span>
                    <span className="fw-medium">{parentAuthorName}</span>
                  </div>
                </>
              )}

              <div className="mb-3">
                <label className="form-label">Content</label>
                <textarea
                  className="form-control"
                  rows={5}
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : isEdit ? (
                  'Save'
                ) : (
                  'Post Reply'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
